/**
 * Returns the relative path segment of `childPath` with respect to `parentPath`.
 * Use this when setting `path` on a nested RouteObject to avoid hardcoding
 * relative segments while keeping `routes.ts` as the single source of truth.
 *
 * @example
 * relativeRoute(routes.music.search, routes.music.searchResults)
 * // => "results"
 *
 * @example
 * relativeRoute(routes.music.playlists, routes.music.playlistsCuratedCategory)
 * // => "curated/:category"
 */
export const relativeRoute = (parentPath: string, childPath: string): string =>
  childPath.replace(`${parentPath}/`, "");

/**
 * Removes any optional path parameters (`/:foo?`) from a route pattern,
 * yielding the base path. Useful when navigating to the parameterless form
 * of a route without pulling react-router-dom into shared-common just for
 * `generatePath(route, { foo: undefined })`.
 *
 * @example
 * stripOptionalParams("/labs/apps/:category?") // => "/labs/apps"
 */
export const stripOptionalParams = (path: string): string =>
  path.replace(/\/:[^/]+\?/g, "");

/**
 * Removes trailing path parameters (`/:foo`, `/:foo?`) from a route pattern,
 * yielding the base path. Useful for navigating to a parent-style route from
 * a parameterised pattern without pulling react-router-dom into shared-common.
 *
 * @example
 * stripTrailingParams("/labs/apps/:category") // => "/labs/apps"
 * stripTrailingParams("/music/track/:id/:tab?") // => "/music/track"
 */
export const stripTrailingParams = (path: string): string =>
  path.replace(/(\/:[^/]+\??)+$/, "");
